<template>
  <v-app>
    <snack-bar></snack-bar>

    <v-main>
      <top-bar v-if="$store.state.auth && $route.path !== '/login'"></top-bar>

      <router-view :class="{'admin-page': $route.path !== '/login'}"/>
    </v-main>

    <v-overlay
        :value="$store.state.loading"
        :opacity="0.8"
        style="z-index: 999"
    >
      <v-progress-circular
          :size="100"
          :width="7"
          color="#00b2ff"
          indeterminate
      ></v-progress-circular>
    </v-overlay>

  </v-app>
</template>

<script>
import SnackBar from "@/components/Layout/SnackBar";
import TopBar from "@/components/Layout/TopBar";

export default {
  name: 'App',

  components: {TopBar, SnackBar},

  data: () => ({
    user: null
  }),

  mounted() {
    this.getAuthStatus()
    this.handleResize()
  },

  methods: {
    getAuthStatus() {
      let token = this.$cookies.get('token')

      if (token) {
        this.$store.dispatch("getProfile")
            .then(() => {
              if (this.$route.path === '/login') {
                this.$router.push('/')
              }
            })
            .catch(() => {
              this.$cookies.remove('token')
              if (this.$route.path !== '/login') {
                this.$router.push('/login')
              }
            })
      } else {
        if (this.$route.path !== '/login') {
          this.$router.push('/login')
        }
      }
    },

    handleResize() {
      let self = this;
      self.$store.commit("setWindowIsSmallerThanMobile", window.innerWidth <= 768)
      self.$store.commit("setWindowIsSmallerThanTablet", window.innerWidth <= 1024)
      self.$store.commit("setWindowIsSmallerThanLargeTablet", window.innerWidth <= 1264)
      window.addEventListener("resize", function () {
        self.$store.commit("setWindowIsSmallerThanMobile", window.innerWidth <= 768)
        self.$store.commit("setWindowIsSmallerThanTablet", window.innerWidth <= 1024)
        self.$store.commit("setWindowIsSmallerThanLargeTablet", window.innerWidth <= 1264)
      });
    }
  },
}
</script>

<style>
/* Pallet :
    #EEEEEE
    #00ADB5
    #393E46
    #222831
*/

/* --- Design tokens --------------------------------------------------------
   One radius scale for the whole panel: controls (buttons, inputs, menus)
   use --radius-md, surfaces (cards, tables, dialogs) use --radius-lg.
   Round things that are round by nature (fab icon buttons, chips, switches,
   pagination dots) keep their shape. */
:root {
  --c-bg: #222831;
  --c-surface: #393E46;
  --c-surface-sunken: rgba(34, 40, 49, 0.45);
  --c-border: rgba(238, 238, 238, 0.08);
  --c-border-strong: rgba(238, 238, 238, 0.14);
  --c-primary: #00ADB5;
  --c-on-primary: #222831;
  --c-text: #EEEEEE;
  --c-muted: #aab2bd;
  --c-danger: #fb4e4e;

  --radius-sm: 8px;
  --radius-md: 12px;
  --radius-lg: 16px;
}

@font-face {
  font-family: dana;
  src: url("../src/assets/fonts/dana/Dana-Medium.ttf");
}

* {
  font-family: dana;
  color: #EEEEEE;
}

#app {
  background:
    radial-gradient(1200px 500px at 100% 0, rgba(0, 173, 181, 0.08), transparent 60%),
    #222831 !important;
}

/* --- Page shell ---------------------------------------------------------- */
.admin-page {
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 24px 48px;
}

@media (max-width: 600px) {
  .admin-page {
    padding: 0 8px 32px;
  }
}

/* --- Surfaces ------------------------------------------------------------ */
.v-card {
  border-radius: var(--radius-lg) !important;
}

.settings-card {
  background-color: var(--c-surface) !important;
  border: 1px solid var(--c-border) !important;
  margin-bottom: 24px;
  scroll-margin-top: 90px;
  box-shadow: none !important;
}

.settings-card > .v-card__title {
  font-size: 18px;
  padding-bottom: 8px;
}

.settings-card > .v-card__title .v-icon {
  color: var(--c-primary) !important;
}

.settings-card > .v-card__title > .v-icon:first-child {
  margin-left: 10px;
  margin-right: 0;
}

.settings-card > .v-card__subtitle {
  color: var(--c-muted) !important;
}

/* A bordered block inside a card (one list entry, one dialog row, ...). */
.sunken-block {
  border: 1px solid var(--c-border-strong);
  border-radius: 12px;
  background: var(--c-surface-sunken);
}

/* A table that already sits in a card does not need its own frame. */
.settings-card .v-data-table {
  border: 0 !important;
  border-radius: 0 !important;
  background-color: transparent !important;
}

.v-dialog {
  border-radius: var(--radius-lg) !important;
}

.v-dialog > .v-card {
  border: 1px solid var(--c-border);
}

.v-menu__content {
  border-radius: var(--radius-md) !important;
  border: 1px solid var(--c-border);
}

.v-data-table {
  border-radius: var(--radius-lg) !important;
  overflow: hidden;
  border: 1px solid var(--c-border);
  background-color: var(--c-surface) !important;
}

.v-data-table > .v-data-table__wrapper > table > thead > tr > th,
.v-data-table > .v-data-table__wrapper > table > thead > tr > th * {
  color: #00ADB5 !important;
  font-size: 13px !important;
  white-space: nowrap;
}

.v-data-table > .v-data-table__wrapper > table > tbody > tr:hover:not(.v-data-table__expanded__content) {
  background: rgba(0, 173, 181, 0.08) !important;
}

h3 {
  margin: 8px 0 12px;
  padding-right: 12px;
  border-right: 3px solid #00ADB5;
}

::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-thumb {
  background: #393E46;
  border-radius: 8px;
}

/* --- Buttons -------------------------------------------------------------
   .btn-primary   filled teal, dark text  - the one main action of a view
   .btn-secondary outlined teal           - back, cancel, secondary actions
   .btn-danger    outlined red            - destructive actions
   Text inherits from the button so these colours win over the global
   `* { color }` rule above. */
.v-btn__content {
  letter-spacing: normal;
  color: inherit !important;
}

.v-btn__content .v-icon {
  color: inherit !important;
}

.v-btn {
  box-shadow: none!important;
}

.v-application .v-btn:not(.v-btn--fab):not(.v-btn--icon) {
  border-radius: var(--radius-md) !important;
}

.v-application .v-btn.btn-primary {
  background-color: var(--c-primary) !important;
  color: var(--c-on-primary) !important;
}

.v-application .v-btn.btn-primary.v-btn--disabled {
  background-color: rgba(0, 173, 181, 0.35) !important;
}

.v-application .v-btn.btn-secondary {
  border: 1px solid rgba(0, 173, 181, 0.6) !important;
  color: var(--c-primary) !important;
  background-color: transparent !important;
}

.v-application .v-btn.btn-danger {
  border: 1px solid rgba(251, 78, 78, 0.6) !important;
  color: var(--c-danger) !important;
  background-color: transparent !important;
}

.v-application .v-btn.btn-secondary:hover,
.v-application .v-btn.btn-danger:hover {
  background-color: rgba(255, 255, 255, 0.04) !important;
}

/* Row of form actions at the bottom of a card or dialog. */
.form-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: flex-end;
}

.form-actions .v-btn {
  min-width: 140px !important;
}

@media (max-width: 600px) {
  .form-actions .v-btn {
    flex: 1 1 0;
  }
}

/* --- Inputs -------------------------------------------------------------- */
.v-application .v-text-field--outlined > .v-input__control > .v-input__slot,
.v-application .v-text-field--rounded > .v-input__control > .v-input__slot {
  border-radius: var(--radius-md) !important;
}

.v-application .v-text-field--rounded > .v-input__control > .v-input__slot {
  padding: 0 12px !important;
}

.v-application .v-text-field--outlined fieldset {
  border-color: var(--c-border-strong);
}

.v-application .v-text-field--outlined:not(.v-input--is-focused):not(.error--text) > .v-input__control > .v-input__slot:hover fieldset {
  border-color: rgba(0, 173, 181, 0.6);
}

.v-snack__wrapper {
  border-radius: var(--radius-md) !important;
}

.row {
  margin: 0;
}

.dates {
  direction: ltr;
}
</style>